import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SuperHttpClientProvider } from '@app/core/http';

export interface AgronomyOption {
  id: string;
  code?: string;
  displayName?: string;
  label?: string;
  name?: string;
  cropCode?: string;
  agronomicCropCode?: string;
  groupId?: string;
  kind?: 'phenology' | 'campaign';
  selectable?: boolean;
}

export interface AgronomyOptions {
  catalogVersion: string;
  crops: AgronomyOption[];
  varietyGroups: AgronomyOption[];
  varieties: AgronomyOption[];
  phases: AgronomyOption[];
}

export interface AgronomicProfileResponse {
  moduleId: number;
  at: string;
  catalogVersion: string;
  identity: {
    status: 'confirmed' | 'derived_from_balance' | 'unknown';
    agronomicCropCode: string | null;
    balanceCropCode?: string | null;
    validity?: { start: string; endExclusive: string | null } | null;
    source?: string | null;
  };
  variety: { varietyId: string | null; groupId: string | null; freeName: string | null };
}

export interface PhenologyObservation {
  idModulePhenologyObservation: number;
  agronomicCropCode: string;
  phaseCodes: string[];
  observedAt: string;
  validFrom: string;
  validTo: string;
  confirmedByIdUser?: number | null;
}

export interface EstimatedPhenologyResponse {
  settings: { shiftDays: number; cycleStartDate: string | null; cycleStartCropCode?: string | null; validFrom: string | null; validTo: string | null };
  region: string | null;
  estimate: {
    availability: 'available' | 'unknown';
    reason?: string;
    primaryStage: { stageCode: string; label: string; observedPhaseId: string | null } | null;
    adjacentStage: { stageCode: string; label: string; observedPhaseId: string | null } | null;
    confidence?: string;
    modelVersion?: string;
  };
}

@Injectable({ providedIn: 'root' })
export class AgronomicConfigurationService {
  constructor(private $http: SuperHttpClientProvider) {}

  getOptions(cropCode?: string): Observable<AgronomyOptions> {
    const params = cropCode ? { cropCode } : undefined;
    return this.$http.default.get('/admin/agronomy/options', { params });
  }

  getProfile(moduleId: number): Observable<AgronomicProfileResponse> {
    return this.$http.default.get(`/admin/module/${moduleId}/agronomic-profile`);
  }

  saveProfile(moduleId: number, payload: {
    agronomicCropCode: string;
    varietyId: string | null;
    varietyGroupId: string | null;
    varietyFreeText: string | null;
    validFrom: string;
    validTo?: string | null;
  }): Observable<any> {
    return this.$http.default.put(`/admin/module/${moduleId}/agronomic-profile`, payload);
  }

  correctProfileValidFrom(moduleId: number, validFrom: string): Observable<AgronomicProfileResponse> {
    return this.$http.default.patch(`/admin/module/${moduleId}/agronomic-profile`, { validFrom });
  }

  closeProfile(moduleId: number, validTo: string): Observable<any> {
    return this.$http.default.delete(`/admin/module/${moduleId}/agronomic-profile`, { params: { validTo } });
  }

  getObservations(moduleId: number): Observable<{ observations: PhenologyObservation[] }> {
    return this.$http.default.get(`/admin/module/${moduleId}/phenology-observations`);
  }

  createObservation(moduleId: number, payload: {
    agronomicCropCode: string;
    phaseCodes: string[];
    observedAt: string;
    validFrom?: string;
    validTo?: string;
  }): Observable<PhenologyObservation> {
    return this.$http.default.post(`/admin/module/${moduleId}/phenology-observations`, payload);
  }

  closeObservation(moduleId: number, observationId: number, validTo: string): Observable<PhenologyObservation> {
    return this.$http.default.patch(
      `/admin/module/${moduleId}/phenology-observations/${observationId}`,
      { validTo }
    );
  }

  deleteObservation(moduleId: number, observationId: number): Observable<any> {
    return this.$http.default.delete(`/admin/module/${moduleId}/phenology-observations/${observationId}`);
  }

  getEstimatedPhenology(moduleId: number): Observable<EstimatedPhenologyResponse> {
    return this.$http.default.get(`/admin/module/${moduleId}/estimated-phenology`);
  }

  saveEstimatedPhenology(moduleId: number, payload: {
    shiftDays: number;
    cycleStartDate: string | null;
    cycleStartCropCode?: string | null;
    validFrom: string;
  }): Observable<any> {
    return this.$http.default.put(`/admin/module/${moduleId}/estimated-phenology`, payload);
  }
}

/** Format date-only values using local calendar fields; never convert through UTC. */
export function localDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Parse a date-only API value as a local calendar date, never as UTC. */
export function dateOnlyToLocalDate(dateOnly: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateOnly);
  if (!match) throw new RangeError('INVALID_DATE_ONLY');
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day, 12);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    throw new RangeError('INVALID_DATE_ONLY');
  }
  return date;
}

export function addCalendarDays(dateOnly: string, days: number): string {
  const [year, month, day] = dateOnly.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  date.setDate(date.getDate() + days);
  return localDateOnly(date);
}

export function inclusiveEndToExclusive(endInclusive: string): string {
  return addCalendarDays(endInclusive, 1);
}

export function exclusiveEndToInclusive(endExclusive: string): string {
  return addCalendarDays(endExclusive, -1);
}
